import React, { useContext } from 'react'

import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk'
import { Checkbox, Grid, Icon, Pill, Row, Text } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'

import filterContext from '@/components/pages/production/components/context'
import ticketDetailsContext from '@/components/pages/production/components/ticket-details/context'
import { OPTIMIZELY_EVENT } from '@/optimizely/constants'
import { trackEvent } from '@/utils/analytics/ga360'

import styles from './styles.module.scss'

const descriptionTextSize = 'md'
const labelTextSize = 'lg'

const FiltersTypes: React.FC<WithOptimizelyProps> = ({ optimizely }) => {
    const {
        state: filterState,
        selectors: { hasGreatDealsSelector: hasGreatDeals },
        dispatch: { onTypeFilterChangeAction: onFilterChange },
    } = useContext(filterContext)
    const {
        state: detailsState,
        dispatch: { toggleDetails },
    } = useContext(ticketDetailsContext)

    return (
        <Grid className={styles.grid}>
            {hasGreatDeals && (
                <>
                    <Row>
                        <Checkbox
                            label={
                                <Pill
                                    importance="success"
                                    className={classNames(styles.marginLeft, styles.greatDealPill)}
                                >
                                    Great Deal
                                </Pill>
                            }
                            data-testid="filter-types-great-deals-checkbox"
                            checked={filterState.typeFilterState.greatDeal}
                            boxSize="xs"
                            onChange={() => {
                                if (detailsState.listing) {
                                    toggleDetails({ listing: null })
                                }
                                onFilterChange({
                                    filterName: { name: 'greatDeal' },
                                    filterValue: !filterState.typeFilterState.greatDeal,
                                })
                                trackEvent(
                                    'TicketFilterUse',
                                    'Type of Tickets',
                                    `${filterState.typeFilterState.greatDeal ? 'Deselect' : 'Select'} Great Deals`,
                                    undefined,
                                    false,
                                )
                            }}
                        ></Checkbox>
                    </Row>
                    <Text
                        altFont
                        state="muted"
                        size={descriptionTextSize}
                        className={styles.description}
                        data-testid="filter-types-great-deals-text"
                    >
                        {`We've done the math for you, so you can buy with confidence and save.`}
                    </Text>
                </>
            )}
            <Row>
                <Checkbox
                    label={
                        <>
                            {/* TODO: BWCT-246 replace with InstantDeliveryWithContainerIcon */}
                            <div className={styles.circle}>
                                <Icon type="ticket-instant-filled" size="xs" className={styles.instantIcon}></Icon>
                            </div>
                            <Text
                                altFont
                                as="span"
                                size={labelTextSize}
                                className={classNames(styles.instantText, styles.fontWeightRegularBold)}
                            >
                                Instant Delivery
                            </Text>
                        </>
                    }
                    data-testid="filter-types-instant-delivery-checkbox"
                    boxSize="xs"
                    checked={filterState.typeFilterState.instantDelivery}
                    onChange={() => {
                        if (detailsState.listing) {
                            toggleDetails({ listing: null })
                        }
                        optimizely?.track(OPTIMIZELY_EVENT.instantDeliveryCheckboxClick)
                        trackEvent(
                            'TicketFilterUse',
                            'Type of Tickets',
                            `${filterState.typeFilterState.instantDelivery ? 'Deselect' : 'Select'} Instant Delivery`,
                            undefined,
                            false,
                        )
                        onFilterChange({
                            filterName: { name: 'instantDelivery' },
                            filterValue: !filterState.typeFilterState.instantDelivery,
                        })
                    }}
                ></Checkbox>
            </Row>
            <Text
                altFont
                state="muted"
                size={descriptionTextSize}
                className={styles.description}
                data-testid="filter-types-instant-delivery-text"
            >
                Available in your account in minutes.
            </Text>
            <Row>
                <Checkbox
                    label={
                        <>
                            <Icon
                                type="ticket-super-seller"
                                size="sm"
                                className={classNames(styles.superSellerIcon, styles.marginLeft)}
                            ></Icon>
                            <Text altFont as="span" size={labelTextSize} className={styles.fontWeightRegularBold}>
                                Super Seller
                            </Text>
                        </>
                    }
                    data-testid="filter-types-super-seller-checkbox"
                    boxSize="xs"
                    checked={filterState.typeFilterState.superSeller}
                    onChange={() => {
                        if (detailsState.listing) {
                            toggleDetails({ listing: null })
                        }
                        optimizely?.track(OPTIMIZELY_EVENT.superSellerCheckboxClick)
                        trackEvent(
                            'TicketFilterUse',
                            'Type of Tickets',
                            `${filterState.typeFilterState.superSeller ? 'Deselect' : 'Select'} Super Seller`,
                            undefined,
                            false,
                        )
                        onFilterChange({
                            filterName: { name: 'superSeller' },
                            filterValue: !filterState.typeFilterState.superSeller,
                        })
                    }}
                ></Checkbox>
            </Row>
            <Text
                altFont
                state="muted"
                size={descriptionTextSize}
                className={styles.description}
                data-testid="filter-types-super-seller-text"
            >
                Super sellers are experienced, highly rated sellers who are committed to providing exceptional tickets
                and service.
            </Text>
            <Row>
                <Checkbox
                    label={
                        <Text
                            altFont
                            as="span"
                            size={labelTextSize}
                            className={classNames(styles.marginLeft, styles.fontWeightRegularBold)}
                        >
                            Recently Viewed
                        </Text>
                    }
                    data-testid="filter-types-recently-viewed-checkbox"
                    boxSize="xs"
                    checked={filterState.typeFilterState.recentlyViewed}
                    onChange={() => {
                        if (detailsState.listing) {
                            toggleDetails({ listing: null })
                        }
                        onFilterChange({
                            filterName: { name: 'recentlyViewed' },
                            filterValue: !filterState.typeFilterState.recentlyViewed,
                        })
                    }}
                ></Checkbox>
            </Row>
            <Text
                altFont
                state="muted"
                size={descriptionTextSize}
                className={styles.bottomDescription}
                data-testid="filter-types-recently-viewed-text"
            >
                Show your previously viewed tickets and pick up where you left off.
            </Text>
        </Grid>
    )
}

export default withOptimizely(FiltersTypes)
